<template>
  <div>
    <slot
      :add-listeners="addListeners"
      :delete-file="deleteFile"
      :drag-over="dragOver"
      :edit="edit"
      :error="error"
      :files="files"
      :format-file-size="formatFileSize"
      :get-file-preview="getFilePreview"
      :handle-drag-leave="handleDragLeave"
      :handle-drag-over="handleDragOver"
      :handle-drop="handleDrop"
      :handle-file-select="handleFileSelect"
      :handle-upload="handleUpload"
      :readers="readers"
      :reset="reset"
      :resize="resize"
      :trigger-file-input="triggerFileInput"
      :unique-i-d="uniqueID"
    >
      <DragnDropArea
        v-if="files.length <= 0 && isEmptyModelValue"
        :description="description"
        :drag-over="dragOver"
        :vertical="vertical"
      />

      <StoredFilePreview
        v-for="(storedFile, storedIndex) in storedFiles"
        :key="storedFile.id ?? storedFile.file_path ?? storedFile.url ?? storedIndex"
        :file="storedFile"
        :disabled="disabled"
        @replace="replaceStoredFile(storedFile)"
        @remove="removeStoredFile(storedFile)"
      />

      <template
        v-for="(reader, readerIndex) in readers"
        :key="readerIndex"
      >
        <UploadingInfo
          :class="{ hidden: reader?.showPreview }"
          :current-progress="reader?.currentProgress"
          :file="files[readerIndex]"
          :index="readerIndex"
          :total-size="reader?.totalSize"
        />
        <FilePreview
          :file="files[readerIndex]"
          :index="readerIndex"
          :show-preview="reader?.showPreview"
        />
      </template>

      <UploadErrorMessage :message="error.message" />
    </slot>

    <input
      :id="uniqueID"
      :ref="uniqueID"
      :accept="accept"
      :multiple="multiple"
      aria-hidden="true"
      class="hidden"
      type="file"
      @change="handleFileSelect"
    >
  </div>
</template>

<script lang="ts">
import uniqueId from 'lodash/uniqueId'
import { toast } from 'vue-sonner'
import { defineComponent } from 'vue'
import DragnDropArea from './DragnDropArea.vue'
import UploadingInfo from './UploadingInfo.vue'
import UploadErrorMessage from './UploadErrorMessage.vue'
import FilePreview from './FilePreview.vue'
import StoredFilePreview from './StoredFilePreview.vue'
import { isStoredFile } from '@/lib/files.js'

interface UploadReader {
  reader: FileReader
  showPreview: boolean
  totalSize: number
  currentProgress: number
  filePreview?: string | ArrayBuffer | null
}

export default defineComponent({
  components: {
    DragnDropArea,
    UploadingInfo,
    UploadErrorMessage,
    FilePreview,
    StoredFilePreview
  },

  provide() {
    return {
      uniqueID: this.uniqueID,
      dragOver: this.dragOver,
      file: this.files,
      error: this.error,
      readers: this.readers,
      // functions
      edit: this.edit,
      deleteFile: this.deleteFile,
      resize: this.resize,
      reset: this.reset,
      handleDragOver: this.handleDragOver,
      handleDragLeave: this.handleDragLeave,
      handleDrop: this.handleDrop,
      triggerFileInput: this.triggerFileInput,
      handleFileSelect: this.handleFileSelect,
      handleUpload: this.handleUpload,
      addListeners: this.addListeners,
      formatFileSize: this.formatFileSize,
      getFilePreview: this.getFilePreview
    }
  },

  props: {
    modelValue: {
      type: [Array, Object, String],
      default: null
    },
    accept: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    maxFileSize: {
      type: Number,
      default: 5000 // 5MB
    },
    multiple: {
      type: Boolean,
      default: false
    },
    maxFiles: {
      type: [Number, String],
      default: 10
    },
    vertical: {
      type: Boolean,
      default: false
    },
    disabled: { type: Boolean, default: false }
  },

  emits: ['fileSelected', 'error', 'update:modelValue', 'delete', 'resize'],

  data(): {
    uniqueID: string
    dragOver: boolean
    readers: UploadReader[]
    files: File[]
    error: any
  } {
    return {
      uniqueID: uniqueId('uploader-'),
      dragOver: false,
      readers: [],
      files: [],
      error: {}
    }
  },

  computed: {
    isEmptyModelValue() {
      const value = this.modelValue
      if (value == null || value === '') return true
      if (Array.isArray(value)) return value.length === 0
      return false
    },

    /** Server-persisted uploads already in modelValue (edit mode) — rendered as their own tiles. */
    storedFiles() {
      const value = this.modelValue
      const list = Array.isArray(value) ? value : value ? [value] : []
      return list.filter((f) => isStoredFile(f))
    },
  },

  created() {
    // Remount (e.g. multi-step wizard) must restore File objects from v-model —
    // they live on the parent form, but previews live in this component's state.
    this.hydrateFromModelValue()
  },

  watch: {
    modelValue() {
      if (this.isEmptyModelValue) {
        if (this.files.length) {
          this.files = []
          this.readers = []
        }
        return
      }

      const list = Array.isArray(this.modelValue)
        ? this.modelValue
        : this.modelValue
          ? [this.modelValue]
          : []
      const fileList = list.filter((f) => f instanceof File)

      // Parent swapped to persisted URL objects after draft — drop local previews
      // so FileInfo (existingFiles) is the single source of truth.
      if (!fileList.length) {
        if (this.files.length) {
          this.files = []
          this.readers = []
        }
        return
      }

      // Parent still has Files but we were remounted empty / wiped — restore.
      if (!this.files.length) {
        this.hydrateFromModelValue()
      }
    },
  },

  beforeUnmount() {
    // Note: `filePreview` is not a declared data property on this component
    // (this check has always been a no-op); preserved as-is, only typed defensively.
    const preview = (this).filePreview
    if (preview) {
      URL.revokeObjectURL(preview)
    }
    this.readers.forEach((r) => {
      if (typeof r.filePreview === 'string' && r.filePreview.startsWith('blob:')) {
        URL.revokeObjectURL(r.filePreview)
      }
    })
  },

  methods: {
    /** Restore local File[] from parent v-model after remount / step change. */
    hydrateFromModelValue() {
      const value = this.modelValue
      const list = Array.isArray(value) ? value : value ? [value] : []
      const fileList = list.filter((f) => f instanceof File)
      if (!fileList.length || this.files.length) return

      fileList.forEach((file) => {
        this.files.push(file)
        this.readers.push({
          reader: new FileReader(),
          showPreview: true,
          totalSize: file.size,
          currentProgress: 100,
          filePreview: URL.createObjectURL(file),
        })
      })
    },

    handleDragOver() {
      this.dragOver = true
    },

    handleDragLeave() {
      this.dragOver = false
    },

    handleDrop(event: DragEvent) {
      if (this.disabled) {
        return
      }
      this.dragOver = false
      this.handleUpload(event.dataTransfer?.files)
    },

    triggerFileInput() {
      if (this.disabled) {
        return
      }
      (this.$refs[this.uniqueID] as HTMLInputElement | undefined)?.click()
    },

    handleFileSelect(event: Event) {
      this.handleUpload((event.target as HTMLInputElement).files)
    },

    handleUpload(e: FileList | File[] | null | undefined) {
      if (this.disabled) return
      let files: File[] = Array.from(e || [])
      if (!files.length) return

      if (!this.multiple) {
        this.readers = []
        this.files = []
        files = [files[0]]
      } else {
        const totalFiles = this.files.length + files.length
        if (totalFiles > Number(this.maxFiles)) {
          toast.error(`Max ${this.maxFiles} files allowed only`)
          const allowedNewFiles = Math.max(0, Number(this.maxFiles) - this.files.length)
          files = files.slice(0, allowedNewFiles)
          if (!files.length) return
        }
      }

      setTimeout(() => {
        const startIndex = this.readers.length
        files.forEach((file, i) => {
          const reader = new FileReader()
          this.readers.push({
            reader,
            showPreview: false,
            totalSize: 0,
            currentProgress: 0
          })

          this.$nextTick(() => {
            this.handleFileUpload(file, startIndex + i)
            this.addListeners(reader, startIndex + i)
            reader.readAsDataURL(file)
          })
        })

        this.resetInputVal()
      })
    },

    addListeners(reader: FileReader, index: number) {
      reader.addEventListener('loadstart', (e) => this.handleEvent(e, index))
      reader.addEventListener('load', (e) => this.handleEvent(e, index))
      reader.addEventListener('loadend', (e) => this.handleEvent(e, index))
      reader.addEventListener('progress', (e) => this.handleEvent(e, index))
      reader.addEventListener('error', (e) => this.handleEvent(e, index))
      reader.addEventListener('abort', (e) => this.handleEvent(e, index))
    },

    handleEvent(event: ProgressEvent<FileReader>, index: number) {
      const currentReader = this.readers[index]
      if (!currentReader) return

      if (event.type === 'error') {
        this.error = currentReader.reader.error
      }

      if (['loadend', 'load'].includes(event.type)) {
        currentReader.filePreview = currentReader.reader.result
        setTimeout(() => {
          currentReader.showPreview = true
        }, 500)
      }

      // `total` is 0 when the size is unknown; skip instead of showing NaN%.
      if (event.type === 'progress' && event.lengthComputable && event.total > 0) {
        currentReader.currentProgress = Math.round((event.loaded / event.total) * 100)
      }

      if (event.type === 'loadstart') {
        currentReader.totalSize = event.total
      }
    },

    handleFileUpload(file: File) {
      this.error = {}
      if (this.validateFile(file)) {
        this.files.push(file)
        // Always emit a copy — sharing `this.files` with the parent means a
        // later local clear (or remount) can desync the form answer / preview.
        this.$emit('fileSelected', [...this.files])
        this.emitFiles()
      }
    },

    validateFile(file: File) {
      if (file.size > this.maxFileSize * 1024 * 1024) {
        this.error = {
          message: `File ${file.name} exceeds maximum size of ${this.formatFileSize(this.maxFileSize)}`
        }
        this.$emit('error', this.error.message)
        return false
      }

      if (this.accept) {
        const fileType = file.type
        const acceptedTypes = this.accept.split(',').map((type: string) => type.trim())

        if (
          !acceptedTypes.some((type: string) => {
            return fileType.match(type) || file.name.toLowerCase().endsWith(type.toLowerCase())
          })
        ) {
          this.error = {
            message: `Please upload ${this.accept}`
          }
          this.$emit('error', this.error.message)
          return false
        }
      }

      return true
    },

    formatFileSize(bytes: number) {
      if (!bytes) return
      const units = ['B', 'KB', 'MB', 'GB']
      let size = bytes
      let unitIndex = 0

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }

      return `${size.toFixed(1)} ${units[unitIndex]}`
    },

    getFilePreview(file: File) {
      if (!file) return ''
      return URL.createObjectURL(file)
    },

    removeFile(index: number) {
      this.readers[index]?.reader.abort()
      this.$nextTick(() => {
        this.files.splice(index, 1)
        this.readers.splice(index, 1)
        this.emitFiles()
      })
    },

    deleteFile(index: number) {
      const preview = this.readers[index]?.filePreview
      if (typeof preview === 'string' && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview)
      }
      this.files.splice(index, 1)
      this.readers.splice(index, 1)
      this.$emit('delete')
      this.emitFiles()
      if (!this.multiple) {
        this.reset()
      }
      this.resetInputVal()
    },

    emitFiles() {
      if (this.multiple) {
        this.$emit('update:modelValue', [...this.files])
      } else {
        this.$emit('update:modelValue', this.files[0] || null)
      }
    },

    resize(index: number) {
      this.$emit('resize', index)
    },

    resetInputVal() {
      const input = this.$refs[this.uniqueID] as HTMLInputElement | undefined
      if (input) {
        input.value = null
      }
    },

    reset() {
      this.readers.forEach((r) => {
        if (typeof r.filePreview === 'string' && r.filePreview.startsWith('blob:')) {
          URL.revokeObjectURL(r.filePreview)
        }
      })
      this.files = []
      this.readers = []
      this.error = {}
      this.emitFiles()
    },

    /** Edit = replace: drop the file at `index`, then open the picker. */
    edit(index: string | number = 0) {
      const i = index === '' || index == null ? 0 : Number(index)
      if (!Number.isNaN(i) && i >= 0 && i < this.files.length) {
        const preview = this.readers[i]?.filePreview
        if (typeof preview === 'string' && preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview)
        }
        this.files.splice(i, 1)
        this.readers.splice(i, 1)
        this.emitFiles()
      } else if (!this.multiple && this.files.length) {
        this.files = []
        this.readers = []
        this.emitFiles()
      }
      this.$nextTick(() => {
        this.resetInputVal()
        this.triggerFileInput()
      })
    },

    /** Drop one stored (already-persisted) entry from modelValue — independent of the local File[] pipeline. */
    removeStoredFile(file: any) {
      const value = this.modelValue
      const list = Array.isArray(value) ? value : value ? [value] : []
      const next = list.filter((f) => f !== file)
      this.$emit('update:modelValue', this.multiple ? next : (next[0] ?? null))
      this.$emit('delete')
    },

    replaceStoredFile(file: any) {
      this.removeStoredFile(file)
      this.$nextTick(() => {
        this.resetInputVal()
        this.triggerFileInput()
      })
    }
  }
})
</script>
